import type { BN } from '@morfi/contract-sdk';
import type { PublicKey } from '@solana/web3.js';
import {
  type MarketEditorialCreateTransport,
  postMarketEditorialCreate,
} from '../tx-engine/relay/market-editorial.js';
import type { ActiveWallet } from '../tx-engine/types.js';
import type { CreateMarketOutcomeParams, CreateMarketParams } from './types.js';

export function assertEditorialCreatePreconditions(params: CreateMarketParams): void {
  const payload = params.editorialCreatePayload;
  if (payload === undefined) return;
  if (params.marketNonceDecimal === undefined) {
    throw new Error('editorialCreatePayload requires marketNonceDecimal');
  }
  if (payload.marketNonceDecimal !== params.marketNonceDecimal) {
    throw new Error('editorialCreatePayload.marketNonceDecimal must match marketNonceDecimal');
  }
  if (payload.outcomes.length !== params.outcomes.length) {
    throw new Error('editorialCreatePayload.outcomes length must match outcomes length');
  }
}

export async function postEditorialCreateSnapshot(args: {
  readonly params: CreateMarketParams;
  readonly activeWallet: ActiveWallet;
  readonly marketNonce: BN;
  readonly marketPubkey: string;
  readonly outcomeMints: readonly PublicKey[];
  readonly transport: MarketEditorialCreateTransport | undefined;
  readonly signal: AbortSignal | undefined;
}): Promise<readonly CreateMarketOutcomeParams[]> {
  const payload = args.params.editorialCreatePayload;
  if (payload === undefined) return args.params.outcomes;
  if (args.transport === undefined) {
    throw new Error('editorialCreatePayload requires editorialCreateTransport');
  }

  const metadataUris = await postMarketEditorialCreate({
    payload: {
      ...payload,
      outcomes: payload.outcomes.map((outcome, index) => {
        const mint = args.outcomeMints[index];
        if (mint === undefined) throw new Error(`Missing outcome mint ${String(index)}`);
        return { ...outcome, outcomeMintBase58: mint.toBase58() };
      }),
    },
    activeWallet: args.activeWallet,
    marketNonce: args.marketNonce,
    marketPubkey: args.marketPubkey,
    outcomeMints: args.outcomeMints,
    chainParams: args.params,
    transport: args.transport,
    signal: args.signal,
  });
  return args.params.outcomes.map((outcome, index) => {
    const uri = metadataUris[index];
    if (uri === undefined) throw new Error(`Missing pinned metadata for outcome ${String(index)}`);
    return { ...outcome, uri };
  });
}
